import React, { useEffect, useState } from 'react';
import {
  createSettlement,
  fetchSettlements,
  fetchProofs,
  createProof,
  type Settlement as SettlementT,
  type FinanceProof,
} from '../api/finance';

export default function Settlements() {
  const [settlements, setSettlements] = useState<SettlementT[]>([]);
  const [proofs, setProofs] = useState<FinanceProof[]>([]);
  const [tab, setTab] = useState<'settlements' | 'proofs'>('settlements');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; msg: string } | null>(null);

  const [form, setForm] = useState({
    escrowId: '',
    agentId: '',
    amount: '',
    token: 'AGU',
  });

  const load = async () => {
    try {
      const [s, p] = await Promise.all([fetchSettlements(), fetchProofs()]);
      setSettlements(s);
      setProofs(p);
    } catch {
      /* keep current */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSettle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.escrowId || !form.agentId || !form.amount) {
      setAlert({ type: 'error', msg: 'All fields are required.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      await createSettlement({
        escrowId: form.escrowId,
        agentId: form.agentId,
        amount: Number(form.amount),
        token: form.token,
        status: 'pending',
      });
      setAlert({ type: 'success', msg: 'Settlement created.' });
      setForm({ escrowId: '', agentId: '', amount: '', token: 'AGU' });
      await load();
    } catch (e) {
      setAlert({ type: 'error', msg: String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s: string) => {
    const cls = s === 'pending' ? 'warning' : s === 'completed' ? 'success' : 'info';
    return <span className={`finance-badge ${cls}`}>{s}</span>;
  };

  /* ── Settlement Timeline Visual ── */
  const TIMELINE = [
    { step: '1', label: 'Task Assigned', desc: 'Agent receives task & escrow is locked' },
    { step: '2', label: 'Execution', desc: 'Agent performs work, proof generated' },
    { step: '3', label: 'Verification', desc: 'Verifiers validate execution proof' },
    { step: '4', label: 'Settlement', desc: 'Funds released to agent' },
  ];

  return (
    <div>
      <header className="finance-header">
        <h1>Settlements & Proofs</h1>
        <p>Settlement lifecycle and verification proof management</p>
      </header>

      {alert && (
        <div className={`finance-alert ${alert.type}`}>{alert.msg}</div>
      )}

      {/* ── Timeline ── */}
      <div className="finance-card" style={{ marginBottom: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">⏱️</span>
            Settlement Lifecycle
          </span>
        </div>
        <div className="finance-timeline">
          {TIMELINE.map((t, i) => (
            <div className="finance-timeline-step" key={i}>
              <div className="finance-timeline-marker">{t.step}</div>
              <div>
                <strong>{t.label}</strong>
                <p className="finance-stat-label">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tab Switch ── */}
      <div className="finance-tabs" style={{ marginBottom: 24 }}>
        <button
          className={`finance-tab ${tab === 'settlements' ? 'active' : ''}`}
          onClick={() => setTab('settlements')}
        >
          ⚡ Settlements ({settlements.length})
        </button>
        <button
          className={`finance-tab ${tab === 'proofs' ? 'active' : ''}`}
          onClick={() => setTab('proofs')}
        >
          🔐 Proofs ({proofs.length})
        </button>
      </div>

      {tab === 'settlements' ? (
        <>
          {/* ── Create Settlement ── */}
          <div className="finance-card" style={{ marginBottom: 24 }}>
            <div className="finance-card-header">
              <span className="finance-card-title">
                <span className="finance-icon">⚡</span>
                Create Settlement
              </span>
            </div>
            <form onSubmit={handleSettle} className="finance-form">
              <div className="finance-form-row">
                <div className="finance-form-group">
                  <label className="finance-label">Escrow ID</label>
                  <input
                    className="finance-input"
                    placeholder="escrow-001"
                    value={form.escrowId}
                    onChange={(e) => setForm({ ...form, escrowId: e.target.value })}
                  />
                </div>
                <div className="finance-form-group">
                  <label className="finance-label">Agent ID</label>
                  <input
                    className="finance-input"
                    placeholder="agent-alpha"
                    value={form.agentId}
                    onChange={(e) => setForm({ ...form, agentId: e.target.value })}
                  />
                </div>
              </div>
              <div className="finance-form-row">
                <div className="finance-form-group">
                  <label className="finance-label">Amount</label>
                  <input
                    className="finance-input"
                    type="number"
                    placeholder="500"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
                <div className="finance-form-group">
                  <label className="finance-label">Token</label>
                  <input
                    className="finance-input"
                    value={form.token}
                    onChange={(e) => setForm({ ...form, token: e.target.value })}
                  />
                </div>
              </div>
              <button className="finance-button" type="submit" disabled={submitting}>
                {submitting ? 'Creating…' : '⚡ Create Settlement'}
              </button>
            </form>
          </div>

          {/* ── Settlement List ── */}
          <div className="finance-card">
            <div className="finance-card-header">
              <span className="finance-card-title">
                <span className="finance-icon">📋</span>
                All Settlements
              </span>
              <span className="finance-badge info">{settlements.length}</span>
            </div>
            {loading ? (
              <div className="finance-loading">Loading…</div>
            ) : settlements.length === 0 ? (
              <div className="finance-empty">No settlements yet.</div>
            ) : (
              <div className="finance-table-wrap">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Escrow</th>
                      <th>Agent</th>
                      <th>Amount</th>
                      <th>Token</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.map((s, i) => (
                      <tr key={s.id || i}>
                        <td className="mono">{s.escrowId}</td>
                        <td className="mono">{s.agentId}</td>
                        <td>{s.amount}</td>
                        <td>{s.token}</td>
                        <td>{statusBadge(s.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        /* ── Proofs Tab ── */
        <div className="finance-card">
          <div className="finance-card-header">
            <span className="finance-card-title">
              <span className="finance-icon">🔐</span>
              Verification Proofs
            </span>
            <span className="finance-badge success">{proofs.length}</span>
          </div>
          {loading ? (
            <div className="finance-loading">Loading…</div>
          ) : proofs.length === 0 ? (
            <div className="finance-empty">No proofs recorded.</div>
          ) : (
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Settlement</th>
                    <th>Hash</th>
                    <th>Verifier</th>
                  </tr>
                </thead>
                <tbody>
                  {proofs.map((p, i) => (
                    <tr key={p.id || i}>
                      <td className="mono">{p.settlementId}</td>
                      <td className="mono">{p.hash}</td>
                      <td className="mono">{p.verifier}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Revenue Split Model ── */}
      <div className="finance-card" style={{ marginTop: 24 }}>
        <div className="finance-card-header">
          <span className="finance-card-title">
            <span className="finance-icon">📊</span>
            Revenue Split Model
          </span>
        </div>
        <div className="finance-splits">
          {[
            { label: 'Agent Core', pct: 40, color: 'var(--finance-green)' },
            { label: 'Verifiers', pct: 30, color: 'var(--finance-blue)' },
            { label: 'Platform', pct: 20, color: 'var(--finance-gold)' },
            { label: 'Insurance Pool', pct: 10, color: 'var(--finance-red)' },
          ].map((s) => (
            <div className="finance-split-item" key={s.label}>
              <div className="finance-split-header">
                <span>{s.label}</span>
                <span style={{ color: s.color, fontWeight: 700 }}>{s.pct}%</span>
              </div>
              <div className="finance-progress">
                <div
                  className="finance-progress-bar"
                  style={{ width: `${s.pct}%`, background: s.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
